/* UtilityAdmin → Documents: generate per-student printable documents
   (Transfer Certificate, Bonafide, Admission Ticket, TC Requisition, RTE letter). */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Printer, Download, Sparkles, UserCog } from "lucide-react";
import { toast } from "sonner";
import { DOC_TYPES, EXTRA_FIELDS, buildForm, renderDoc } from "./documentTemplates";

function useClassSections() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/sections")
      .then(({ data }) => setRows((data.sections || data || []).filter((r) => r.class_name && r.section)))
      .catch(() => setRows([]));
  }, []);
  const classes = useMemo(() => [...new Set(rows.map((r) => r.class_name))], [rows]);
  const sectionsFor = (cls) => [...new Set(rows.filter((r) => r.class_name === cls).map((r) => r.section))].sort();
  return { classes, sectionsFor };
}

export default function DocumentsGenerator() {
  const { classes, sectionsFor } = useClassSections();
  const [type, setType] = useState("tc");
  const [cls, setCls] = useState("");
  const [section, setSection] = useState("");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [busy, setBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [docData, setDocData] = useState(null);
  const iframeRef = useRef(null);

  // Extra (reusable) student details editor
  const [extraOpen, setExtraOpen] = useState(false);
  const [extra, setExtra] = useState({});
  const [extraBusy, setExtraBusy] = useState(false);

  const meta = DOC_TYPES.find((t) => t.key === type) || DOC_TYPES[0];
  const schoolLevel = !!meta.schoolLevel;
  const logoUrl = `${window.location.origin}/trishul-logo.png`;

  useEffect(() => {
    setStudentId("");
    if (!cls || !section) { setStudents([]); return; }
    api.get("/utility/students", { params: { class_name: cls, section } })
      .then(({ data }) => setStudents(data.students || []))
      .catch(() => setStudents([]));
  }, [cls, section]);

  const srcDoc = useMemo(
    () => (open ? renderDoc(type, values, logoUrl, docData) : ""),
    [open, type, values, docData, logoUrl],
  );

  const createDocument = async () => {
    if (!schoolLevel && !studentId) return toast.error("Pick a student first.");
    setBusy(true);
    try {
      let data = { student: {}, school: {}, academic_year: "" };
      if (!schoolLevel && studentId) {
        const res = await api.get("/utility/student-doc-data", { params: { student_id: studentId } });
        data = res.data;
      } else {
        // school-level: fetch just the school header meta (no student needed)
        const res = await api.get("/utility/school-meta");
        data = res.data;
      }
      const f = buildForm(type, data);
      setFields(f);
      setValues(Object.fromEntries(f.map((x) => [x.key, x.default || ""])));
      setDocData(data);
      setOpen(true);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load student data.");
    } finally { setBusy(false); }
  };

  const doPrint = () => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.focus();
    w.print();
  };
  const doDownload = () => {
    toast.info("In the print dialog, choose “Save as PDF” to download.");
    doPrint();
  };

  const openExtraEditor = async () => {
    if (!studentId) return toast.error("Pick a student first.");
    setExtraBusy(true);
    try {
      const res = await api.get("/utility/student-doc-data", { params: { student_id: studentId } });
      setExtra(res.data?.student?.extra || {});
      setExtraOpen(true);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load student details.");
    } finally { setExtraBusy(false); }
  };
  const saveExtra = async () => {
    setExtraBusy(true);
    try {
      await api.put("/utility/student-extra", { student_id: studentId, extra });
      toast.success("Saved. These details will auto-fill in all documents for this student.");
      setExtraOpen(false);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save details.");
    } finally { setExtraBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="documents-tab">
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Document type</Label>
          <Select value={type} onValueChange={(v) => { setType(v); }}>
            <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`doc-type-${t.key}`}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">{meta.desc}</p>
        </div>

        {!schoolLevel && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Class</Label>
                <Select value={cls} onValueChange={(v) => { setCls(v); setSection(""); }}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="doc-class"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Section</Label>
                <Select value={section} onValueChange={setSection} disabled={!cls}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="doc-section"><SelectValue placeholder="Section" /></SelectTrigger>
                  <SelectContent>{sectionsFor(cls).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">Student</Label>
              <Select value={studentId} onValueChange={setStudentId} disabled={!students.length}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="doc-student"><SelectValue placeholder={students.length ? "Select student" : "Pick class & section"} /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.roll_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </>
        )}

        <Button onClick={createDocument} disabled={busy || (!schoolLevel && !studentId)} className="rounded-xl" data-testid="doc-create">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} Create document
        </Button>
        {!schoolLevel && (
          <Button variant="outline" onClick={openExtraEditor} disabled={extraBusy || !studentId} className="rounded-xl ml-2" data-testid="doc-edit-extra">
            {extraBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCog className="h-4 w-4 mr-2" />} Edit student details
          </Button>
        )}
        {!schoolLevel && (
          <p className="text-xs text-slate-400">Tip: “Edit student details” saves reusable info (caste, mother's name, admission no, Aadhar…) onto the student so every document auto-fills it.</p>
        )}
      </div>

      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center justify-center text-center text-slate-400 gap-2">
        <Sparkles className="h-8 w-8 text-violet-400" />
        <p className="text-sm">Pick a document type{schoolLevel ? "" : ", class, section and student"}, then <b>Create document</b>. A print-ready preview opens with editable fields — auto-filled from the student record. Missing details (caste, admission date, etc.) can be typed in before printing.</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="doc-preview-dialog">
          <DialogHeader className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base">{meta.label}</DialogTitle>
            <div className="flex gap-2 pr-8">
              <Button size="sm" variant="outline" onClick={doDownload} className="rounded-lg" data-testid="doc-download"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
              <Button size="sm" onClick={doPrint} className="rounded-lg" data-testid="doc-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
            </div>
          </DialogHeader>
          <div className="flex-1 grid md:grid-cols-[320px_1fr] overflow-hidden">
            <div className="border-r border-slate-200 dark:border-slate-800 overflow-y-auto p-4 space-y-3" data-testid="doc-fields">
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Editable fields</p>
              {fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-500 dark:text-slate-400">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea rows={f.key === "schedule" ? 10 : 3} value={values[f.key] || ""} onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))} className="mt-1 text-sm rounded-lg" data-testid={`doc-field-${f.key}`} />
                  ) : (
                    <Input value={values[f.key] || ""} onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))} className="h-9 mt-1 text-sm rounded-lg" data-testid={`doc-field-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="overflow-hidden bg-slate-200">
              <iframe ref={iframeRef} title="Document preview" srcDoc={srcDoc} className="w-full h-full bg-white" data-testid="doc-preview-frame" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-hidden flex flex-col" data-testid="extra-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Student details (auto-fill)</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 overflow-y-auto pr-1" data-testid="extra-fields">
            {EXTRA_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-xs text-slate-500 dark:text-slate-400">{f.label}</Label>
                <Input value={extra[f.key] || ""} onChange={(e) => setExtra((p) => ({ ...p, [f.key]: e.target.value }))} className="h-9 mt-1 text-sm rounded-lg" data-testid={`extra-field-${f.key}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
            <Button variant="outline" onClick={() => setExtraOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={saveExtra} disabled={extraBusy} className="rounded-lg" data-testid="extra-save">
              {extraBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save details
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
